import React, { useState, useEffect } from 'react';
import { Phone, Instagram, Menu, X, ArrowLeft, ArrowRight, Calendar, MapPin, Trophy, ChevronLeft, ChevronRight as ChevronRightIcon } from 'lucide-react';
import axios from 'axios';
import DOMPurify from 'dompurify';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// JSON-LD script breakout'unu engellemek için `<` karakterini escape et
const safeJsonLd = (obj) => JSON.stringify(obj).replace(/</g, '\\u003c');

const NAV = [
  { href: '/#hizmetler', label: 'Hizmetler' },
  { href: '/#organizasyonlar', label: 'Organizasyonlar' },
  { href: '/blog', label: 'Blog' },
  { href: '/#hakkimizda', label: 'Hakkımızda' },
  { href: '/#iletisim', label: 'İletişim' },
];

const PublicHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20">
          <a href="/" className="flex items-center gap-3">
            <img src="/avrasya-logo.jpg" alt="Avrasya Sportif" className="h-12 w-12 object-contain rounded" />
            <div className="leading-tight text-slate-900">
              <p className="text-base font-black tracking-tight" style={{ fontFamily: 'Outfit' }}>AVRASYA</p>
              <p className="text-[10px] tracking-[0.25em] -mt-0.5 opacity-75">SPORTİF FAALİYETLER</p>
            </div>
          </a>
          <div className="hidden md:flex items-center gap-8">
            {NAV.map(item => (
              <a key={item.href} href={item.href} className="text-sm font-medium tracking-wide text-slate-700 hover:text-[#0052A5] transition-colors">{item.label}</a>
            ))}
            <a href="/#iletisim" className="bg-[#0052A5] hover:bg-[#003D7A] text-white px-5 py-2.5 text-sm font-semibold tracking-wide" style={{ fontFamily: 'Outfit' }}>BİZE ULAŞIN</a>
          </div>
          <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-slate-900">{menuOpen ? <X /> : <Menu />}</button>
        </div>
      </div>
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 shadow-lg">
          <div className="px-6 py-4 space-y-3">
            {NAV.map(item => (<a key={item.href} href={item.href} onClick={() => setMenuOpen(false)} className="block py-2 text-slate-700 hover:text-[#0052A5] font-medium">{item.label}</a>))}
          </div>
        </div>
      )}
    </nav>
  );
};

const PublicFooter = () => (
  <footer className="bg-black text-white relative overflow-hidden">
    <p className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-[20vw] font-black tracking-tighter text-white/[0.03] pointer-events-none select-none whitespace-nowrap" style={{ fontFamily: 'Outfit' }}>AVRASYA</p>
    <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 py-12">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <img src="/avrasya-logo.jpg" alt="Avrasya Sportif" className="h-10 w-10 object-contain rounded" />
            <div className="leading-tight">
              <p className="text-base font-black tracking-tight" style={{ fontFamily: 'Outfit' }}>AVRASYA</p>
              <p className="text-[10px] tracking-[0.25em] -mt-0.5 opacity-50">SPORTİF FAALİYETLER</p>
            </div>
          </div>
          <p className="text-white/50 text-sm max-w-md">Kurumsal etkinlikler, voleybol turnuvaları ve profesyonel canlı yayın çözümleri.</p>
        </div>
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-white/40 mb-3">Menü</p>
          <ul className="space-y-2 text-sm">{NAV.map(item => (<li key={item.href}><a href={item.href} className="text-white/70 hover:text-white">{item.label}</a></li>))}</ul>
        </div>
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-white/40 mb-3">İletişim</p>
          <ul className="space-y-2 text-sm">
            <li><a href="tel:+905053686091" className="text-white/70 hover:text-white inline-flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> 0505 368 60 91</a></li>
            <li><a href="https://instagram.com/avrasyasportif" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white inline-flex items-center gap-2"><Instagram className="w-3.5 h-3.5" /> @avrasyasportif</a></li>
          </ul>
        </div>
      </div>
      <div className="pt-6 border-t border-white/10 text-xs text-white/40">© {new Date().getFullYear()} Avrasya Sportif Faaliyetler. Tüm hakları saklıdır.</div>
    </div>
  </footer>
);

const renderRichText = (text) => {
  if (!text) return '';
  // Light markdown: ## h2, ### h3, **bold**, *italic*, lists, paragraphs
  const lines = text.split('\n');
  const out = [];
  let inList = null;
  let listType = null;
  const flushList = () => {
    if (inList) {
      out.push(`</${listType}>`);
      inList = null; listType = null;
    }
  };
  const inline = (s) =>
    s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
     .replace(/\*([^*]+)\*/g, '<em>$1</em>');
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushList(); continue; }
    if (line.startsWith('### ')) { flushList(); out.push(`<h3>${inline(line.slice(4))}</h3>`); continue; }
    if (line.startsWith('## ')) { flushList(); out.push(`<h2>${inline(line.slice(3))}</h2>`); continue; }
    if (line.startsWith('- ')) {
      if (inList && listType !== 'ul') flushList();
      if (!inList) { out.push('<ul>'); inList = true; listType = 'ul'; }
      out.push(`<li>${inline(line.slice(2))}</li>`);
      continue;
    }
    flushList();
    out.push(`<p>${inline(line)}</p>`);
  }
  flushList();
  return out.join('\n');
};

export const EventDetailPage = ({ slug }) => {
  const [event, setEvent] = useState(null);
  const [otherEvents, setOtherEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    axios.get(`${API}/events/by-slug/${slug}`)
      .then(r => {
        setEvent(r.data);
        document.title = `${r.data.title} | Avrasya Sportif Organizasyonları`;
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
          const desc = (r.data.description || '').slice(0, 160);
          meta.setAttribute('content', desc);
        }
        // Fetch other events for the bottom strip
        axios.get(`${API}/events`).then(rr => {
          setOtherEvents((rr.data || []).filter(e => e.slug !== slug && e.id !== slug).slice(0, 3));
        }).catch(() => {});
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (lightboxIdx === null) return;
    const handler = (e) => {
      if (e.key === 'Escape') setLightboxIdx(null);
      if (e.key === 'ArrowLeft' && event?.gallery) setLightboxIdx((i) => (i - 1 + event.gallery.length) % event.gallery.length);
      if (e.key === 'ArrowRight' && event?.gallery) setLightboxIdx((i) => (i + 1) % event.gallery.length);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIdx, event]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-slate-400">Yükleniyor...</p>
      </div>
    );
  }
  if (notFound || !event) {
    return (
      <div className="min-h-screen bg-white" style={{ fontFamily: '"Manrope", system-ui, sans-serif' }}>
        <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@500;700;800;900&family=Manrope:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
        <PublicHeader />
        <div className="pt-40 pb-20 text-center">
          <h1 className="text-4xl font-black mb-4" style={{ fontFamily: 'Outfit' }}>Organizasyon Bulunamadı</h1>
          <p className="text-slate-500 mb-6">Aradığınız organizasyon sayfası bulunamadı veya yayında değil.</p>
          <a href="/#organizasyonlar" className="inline-flex items-center gap-2 bg-[#0052A5] text-white px-6 py-3 text-sm font-bold tracking-wider hover:bg-[#003D7A]" style={{ fontFamily: 'Outfit' }}>
            <ArrowLeft className="w-4 h-4" /> ORGANİZASYONLARA DÖN
          </a>
        </div>
        <PublicFooter />
      </div>
    );
  }

  const gallery = event.gallery || [];
  const cover = event.image_url;

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: '"Manrope", system-ui, sans-serif' }} data-testid="event-detail-page">
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@500;700;800;900&family=Manrope:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <PublicHeader />

      {/* JSON-LD Schema.org Event */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd({
          "@context": "https://schema.org",
          "@type": "Event",
          "name": event.title,
          "description": event.description,
          "image": event.image_url,
          "startDate": event.date || event.year,
          "location": event.location ? { "@type": "Place", "name": event.location } : undefined,
          "organizer": {
            "@type": "Organization",
            "name": "Avrasya Sportif Faaliyetler",
            "url": "https://avrasyasportif.com",
            "logo": "https://avrasyasportif.com/avrasya-logo.jpg"
          }
        }) }}
      />

      {/* HERO */}
      <section
        className="relative pt-32 pb-20 sm:pb-32 text-white overflow-hidden"
        style={{
          backgroundImage: cover
            ? `linear-gradient(135deg, rgba(0,30,60,0.85) 0%, rgba(0,82,165,0.65) 100%), url(${cover})`
            : 'linear-gradient(135deg, #001E3C 0%, #0052A5 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="relative max-w-5xl mx-auto px-6 sm:px-12 lg:px-16">
          <a href="/#organizasyonlar" className="inline-flex items-center gap-1 text-white/80 hover:text-white text-sm mb-6 transition-colors" data-testid="event-back-link">
            <ArrowLeft className="w-4 h-4" /> Organizasyonlar
          </a>
          <div className="flex items-center gap-3 text-[11px] tracking-[0.25em] uppercase text-white/60 mb-4">
            <span className="inline-flex items-center gap-1"><Trophy className="w-3 h-3" /> Referans</span>
            {event.year && (<><span>•</span><span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{event.year}</span></>)}
            {event.location && (<><span>•</span><span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{event.location}</span></>)}
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl tracking-tight font-black uppercase leading-[1.0]" style={{ fontFamily: 'Outfit' }}>
            {event.title}
          </h1>
          {event.subtitle && (
            <p className="mt-6 text-white/85 text-lg max-w-3xl leading-relaxed">{event.subtitle}</p>
          )}
        </div>
      </section>

      {/* CONTENT */}
      <article className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 lg:px-16">
          {/* Description */}
          <div className="prose-event">
            {event.description && (
              <p className="text-lg text-slate-700 leading-relaxed mb-6 font-medium">{event.description}</p>
            )}
            {event.long_description && (
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderRichText(event.long_description)) }} />
            )}
          </div>

          {/* Gallery */}
          {gallery.length > 0 && (
            <div className="mt-12 pt-12 border-t border-slate-200">
              <p className="text-xs uppercase tracking-[0.3em] text-[#0052A5] font-bold mb-4">— Galeri</p>
              <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight mb-8" style={{ fontFamily: 'Outfit' }}>
                Etkinlik Kareleri
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 lg:gap-4">
                {gallery.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setLightboxIdx(i)}
                    className="group relative aspect-[4/3] overflow-hidden bg-slate-100 cursor-zoom-in"
                    data-testid={`event-gallery-${i}`}
                  >
                    <img src={img} alt={`${event.title} - ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-16 bg-[#0052A5] text-white p-8 sm:p-10 text-center">
            <h3 className="text-2xl sm:text-3xl font-black uppercase tracking-tight mb-3" style={{ fontFamily: 'Outfit' }}>
              Sıradaki Organizasyon Sizin <span className="italic font-light">olsun</span>
            </h3>
            <p className="text-white/80 mb-6 text-sm max-w-xl mx-auto">Şirketinizin spor etkinliği, voleybol turnuvası veya canlı yayın projesi için profesyonel ekibimizle iletişime geçin.</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a href="tel:+905053686091" className="inline-flex items-center gap-2 bg-white text-[#0052A5] px-6 py-3 text-sm font-bold tracking-wider hover:bg-slate-100" style={{ fontFamily: 'Outfit' }}>
                <Phone className="w-4 h-4" /> 0505 368 60 91
              </a>
              <a href="https://instagram.com/avrasyasportif" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border-2 border-white/30 text-white px-6 py-3 text-sm font-bold tracking-wider hover:bg-white/10" style={{ fontFamily: 'Outfit' }}>
                <Instagram className="w-4 h-4" /> @avrasyasportif
              </a>
            </div>
          </div>
        </div>

        <style>{`
          .prose-event { color: #1f2937; line-height: 1.75; font-size: 1.05rem; }
          .prose-event h2 { font-family: 'Outfit', sans-serif; font-weight: 800; font-size: 1.75rem; color: #0F172A; margin-top: 2.5rem; margin-bottom: 1rem; line-height: 1.2; letter-spacing: -0.02em; }
          .prose-event h3 { font-family: 'Outfit', sans-serif; font-weight: 700; font-size: 1.35rem; color: #0F172A; margin-top: 2rem; margin-bottom: 0.75rem; }
          .prose-event p { margin: 1rem 0; }
          .prose-event ul { margin: 1rem 0; padding-left: 1.5rem; list-style: disc; }
          .prose-event li { margin: 0.5rem 0; }
          .prose-event strong { color: #0052A5; font-weight: 700; }
        `}</style>
      </article>

      {/* OTHER EVENTS */}
      {otherEvents.length > 0 && (
        <section className="py-16 bg-[#F4F5F7] border-t border-slate-200">
          <div className="max-w-6xl mx-auto px-6 sm:px-12 lg:px-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold mb-3">— Diğer Organizasyonlar</p>
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight mb-8" style={{ fontFamily: 'Outfit' }}>
              İlginizi Çekebilir
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {otherEvents.map(o => (
                <a
                  key={o.id}
                  href={`/organizasyon/${o.slug || o.id}`}
                  className="group bg-white overflow-hidden border border-slate-200 hover:border-[#0052A5] hover:shadow-lg transition-all"
                  data-testid={`other-event-${o.slug || o.id}`}
                >
                  {o.image_url && (
                    <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                      <img src={o.image_url} alt={o.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-black text-sm uppercase tracking-tight leading-tight mb-1 line-clamp-2 group-hover:text-[#0052A5] transition-colors" style={{ fontFamily: 'Outfit' }}>{o.title}</h3>
                    {o.year && <p className="text-[10px] text-slate-500 tracking-widest uppercase">{o.year}</p>}
                    <div className="mt-2 inline-flex items-center gap-1 text-xs text-[#0052A5] font-bold">
                      Detay <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      <PublicFooter />

      {/* LIGHTBOX */}
      {lightboxIdx !== null && gallery[lightboxIdx] && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightboxIdx(null)}
          data-testid="event-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors"
            data-testid="lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
          {gallery.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx - 1 + gallery.length) % gallery.length); }}
                className="absolute left-4 w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx + 1) % gallery.length); }}
                className="absolute right-4 w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors"
                data-testid="lightbox-next"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </>
          )}
          <img
            src={gallery[lightboxIdx]}
            alt={`${event.title} - ${lightboxIdx + 1}`}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-xs tracking-widest uppercase">
            {lightboxIdx + 1} / {gallery.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetailPage;
